import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AccountsData,
  AccountsEntity,
  AccountsHighlightsEntity,
  AppFeatureKey,
  AppRoleKey,
  COUNTRY_FEATURES,
  CompanyAccountsCompositeEntity,
  CompanyData,
  CompanyDetailsEntity,
  CompanyEntity,
  CompanyFlagHistoryCompositeEntity,
  CompanyShareClassAggregateCompositeEntity,
  CompanyShareClassOwnershipCompositeEntity,
  CompanyShareOwnershipBeneficialCompositeEntity,
  CompanyShareOwnershipDirectCompositeEntity,
  CompanyShareOwnershipIndirectCompositeEntity,
  CompanyShareOwnershipUltimateCompositeEntity,
  PersonData,
  SimpleCollection,
} from '@nk/data-entities-lib';
import { arrayToDict } from '@nk/utils-lib';
import { Observable, forkJoin, of } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';
import { CompanyService, ShareholderParams } from './company.service';
import { PersonService } from './person.service';

export interface CompanyLegalRightParams {
  companyUuid?: string;
  rightsholderCompanyUuid?: string;
  rightsholderPersonUuid?: string;
  legalRightUuid?: string;
}

@Injectable()
export class BusinessService {
  constructor(
    private _companyService: CompanyService,
    private _personService: PersonService,
    private _cachingService: CachingService,
    private _analysisApiAccess: AnalysisApiClientAccess,
  ) {}

  // TODO: move to CompanyOwnershipService
  public companyShareOwnershipUltimateCompositeCollection = this._cachingService.create(
    this,
    this.fetchCompanyShareOwnershipUltimateCompositeCollection,
  );
  public fetchCompanyShareOwnershipUltimateCompositeCollection(
    params: ShareholderParams,
  ): Observable<SimpleCollection<CompanyShareOwnershipUltimateCompositeEntity>> {
    // TODO: Refactor this to use CompanyShareholderParams, when CompanyOwnershipService is created
    let httpParams: HttpParams;
    if (params instanceof ShareholderParams) {
      httpParams = params.getShareholderHttpParams();
    } else {
      httpParams = new ShareholderParams(params).getShareholderHttpParams();
    }
    return this._analysisApiAccess.get<SimpleCollection<CompanyShareOwnershipUltimateCompositeEntity>>(
      'company-share-ownership-ultimate-composite-collection',
      { params: httpParams },
      [
        AppFeatureKey.base,
        AppFeatureKey.historical_ownership,
        AppFeatureKey.norwegian_property_data,
        ...COUNTRY_FEATURES,
      ],
      [AppRoleKey.norwegian_property_data],
    );
  }

  // TODO: move to CompanyOwnershipService
  public companyShareOwnershipIndirectCompositeCollection = this._cachingService.create(
    this,
    this.fetchCompanyShareOwnershipIndirectCompositeCollection,
  );
  public fetchCompanyShareOwnershipIndirectCompositeCollection(
    params: ShareholderParams,
  ): Observable<SimpleCollection<CompanyShareOwnershipIndirectCompositeEntity>> {
    const httpParams = params.getShareholderHttpParams();
    return this._analysisApiAccess.get<SimpleCollection<CompanyShareOwnershipIndirectCompositeEntity>>(
      'company-share-ownership-indirect-composite-collection',
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COUNTRY_FEATURES],
    );
  }

  // TODO: move to CompanyOwnershipService
  public companyShareOwnershipBeneficialCompositeCollection = this._cachingService.create(
    this,
    this.fetchCompanyShareOwnershipBeneficialCompositeCollection,
  );
  public fetchCompanyShareOwnershipBeneficialCompositeCollection(
    params: ShareholderParams,
  ): Observable<SimpleCollection<CompanyShareOwnershipBeneficialCompositeEntity>> {
    const httpParams = params.getShareholderHttpParams();
    return this._analysisApiAccess.get<SimpleCollection<CompanyShareOwnershipBeneficialCompositeEntity>>(
      'company-share-ownership-beneficial-composite-collection',
      { params: httpParams },
      [
        AppFeatureKey.beneficial_ownership,
        AppFeatureKey.historical_ownership,
        AppFeatureKey.base,
        AppFeatureKey.norwegian_property_data,
        ...COUNTRY_FEATURES,
      ],
      [AppRoleKey.norwegian_property_data],
    );
  }

  // TODO: move to CompanyOwnershipService
  public companyShareOwnershipDirectCompositeCollection = this._cachingService.create(
    this,
    this.fetchCompanyShareOwnershipDirectCompositeCollection,
  );
  public fetchCompanyShareOwnershipDirectCompositeCollection(
    params: ShareholderParams,
  ): Observable<SimpleCollection<CompanyShareOwnershipDirectCompositeEntity>> {
    const httpParams = params.getShareholderHttpParams();
    return this._analysisApiAccess.get<SimpleCollection<CompanyShareOwnershipDirectCompositeEntity>>(
      'company-share-ownership-direct-composite-collection',
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COUNTRY_FEATURES],
    );
  }

  // TODO: move to CompanyOwnershipService
  public companyShareClassOwnershipCompositeCollection = this._cachingService.create(
    this,
    this.fetchCompanyShareClassOwnershipCompositeCollection,
  );
  public fetchCompanyShareClassOwnershipCompositeCollection(
    params: ShareholderParams,
  ): Observable<SimpleCollection<CompanyShareClassOwnershipCompositeEntity>> {
    const httpParams = params.getShareholderHttpParams();
    return this._analysisApiAccess.get<SimpleCollection<CompanyShareClassOwnershipCompositeEntity>>(
      'company-share-class-ownership-composite-collection',
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COUNTRY_FEATURES],
    );
  }

  // TODO: move to CompanyOwnershipService
  public companyShareClassAggregateCompositeCache = this._cachingService.create(
    this,
    this.fetchCompanyShareClassAggregateComposite,
  );
  public fetchCompanyShareClassAggregateComposite(
    companyIdentifier: string,
    year: number = null,
  ): Observable<CompanyShareClassAggregateCompositeEntity[]> {
    let params = new HttpParams();
    if (year) {
      params = params.append('year', year);
    }
    return this._analysisApiAccess.get<CompanyShareClassAggregateCompositeEntity[]>(
      `/company/${companyIdentifier}/company-share-class-aggregate-composite`,
      { params: params },
      [AppFeatureKey.base, AppFeatureKey.historical_ownership, ...COUNTRY_FEATURES],
    );
  }

  public companyDataCache = this._cachingService.create(this, this.fetchCompanyData);
  public fetchCompanyData(companyUuid: string): Observable<CompanyData> {
    return forkJoin([
      this._companyService.companyCache.get(companyUuid).pipe(first()),
      this._companyService.companyDetailsCache.get(companyUuid).pipe(first()),
      this.accountsHighlightsDataListByCompanyCache.get(companyUuid).pipe(first()),
    ]).pipe(
      map((payload) => {
        const [company, companyDetails, accountsDataList] = payload;
        return new CompanyData(company, companyDetails.length > 0 ? companyDetails[0] : null, accountsDataList);
      }),
    );
  }

  // TODO: Deprecate
  public personDataCache = this._cachingService.create(this, this.fetchPersonData);
  public fetchPersonData(personUuid: string): Observable<PersonData> {
    return forkJoin([
      this._personService.personCache.get(personUuid).pipe(first()),
      this._personService.personFlagTypeListByPersonCache.get(personUuid).pipe(first()),
      this._personService.personMasterByPerson.get(personUuid).pipe(first()),
    ]).pipe(
      map((payload) => {
        const [person, personFlagTypeDataList, personMasters] = payload;
        return new PersonData(person, personFlagTypeDataList, personMasters[0]);
      }),
    );
  }

  public accountsAndHighlightsByLatestHighlightsYearCache = this._cachingService.create(
    this,
    this.fetchAccountsAndHighlightsByLatestHighlightsYear,
  );
  public fetchAccountsAndHighlightsByLatestHighlightsYear(companyUuid: string): Observable<AccountsWithHighlightsData> {
    let accountsHighlights: AccountsHighlightsEntity;
    return this._companyService.fetchAccountsHighlightsListByCompany(companyUuid, true).pipe(
      mergeMap((accountsHighlightsList) => {
        if (accountsHighlightsList && accountsHighlightsList.length > 0) {
          accountsHighlights = accountsHighlightsList[0];
          return this._companyService.accountsCache.get(accountsHighlights.accounts_uuid);
        } else {
          return of<AccountsEntity>(null);
        }
      }),
      map((accounts) => {
        if (accounts && accountsHighlights) {
          return { accounts, accountsHighlights };
        }
        return null;
      }),
    );
  }

  public accountsHighlightsDataListByCompanyCache = this._cachingService.create(
    this,
    this.fetchAccountsHighlightsDataListByCompany,
  );
  public fetchAccountsHighlightsDataListByCompany(companyUuid: string, latestFlag = false): Observable<AccountsData[]> {
    return forkJoin([
      this._companyService.accountsListByCompanyCache.get(companyUuid, latestFlag).pipe(first()),
      this._companyService.accountsHighlightsListByCompanyCache.get(companyUuid, latestFlag).pipe(first()),
      this._companyService.accountsBalanceSheetListByCompanyCache.get(companyUuid, latestFlag).pipe(first()),
    ]).pipe(
      map((payloads) => {
        const [accountsList, accountsHighlightsList, accountsBalanceSheetList] = payloads;
        const accountsHighlightsListByAccountsUuid = arrayToDict(accountsHighlightsList, (high) => high.accounts_uuid);
        const accountsBalanceSheetListByAccountsUuid = arrayToDict(
          accountsBalanceSheetList,
          (bal) => bal.accounts_uuid,
        );
        return accountsList.map(
          (accounts) =>
            new AccountsData(
              accounts,
              accountsHighlightsListByAccountsUuid[accounts.uuid],
              null,
              accountsBalanceSheetListByAccountsUuid[accounts.uuid],
            ),
        );
      }),
    );
  }

  public accountsCompositeByCompany = this._cachingService.create(this, this.fetchAccountsComposite);
  public fetchAccountsComposite(
    companyUuid: string,
    accountsTypeIdentifier: string = null,
    accountingYear: number = null,
    accountingYearList: number[] = null,
  ): Observable<CompanyAccountsCompositeEntity[]> {
    let params = new HttpParams();
    if (accountsTypeIdentifier) {
      params = params.append('accounts_type_identifier', accountsTypeIdentifier);
    }
    if (accountingYear) {
      params = params.append('accounting_year', String(accountingYear));
    }
    if (accountingYearList) {
      const accountingYearsParam = accountingYearList.join(',');
      params = params.append('accounting_years', String(accountingYearsParam));
    }
    return this._analysisApiAccess.get<CompanyAccountsCompositeEntity[]>(
      `/company/${companyUuid}/accounts-composite`,
      { params: params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }
}

// deprecated, use CompanyAccountsHighlightsCompositeEntity instead
export interface AccountsWithHighlightsData {
  accounts: AccountsEntity;
  accountsHighlights: AccountsHighlightsEntity;
}

export interface CompanyHighlightsData {
  company: CompanyEntity;
  accounts: AccountsWithHighlightsData;
  companyDetails: CompanyDetailsEntity;
  companyFlagHistoryComposite: CompanyFlagHistoryCompositeEntity[];
}
